import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import camera from "../assets/images/icons/camera2.svg";
import check from "../assets/images/icons/check.svg";
import characterVideoHevc from "../assets/videos/rotating-iphone-prores-with-alpha.mp4";
import characterVideoWebm from "../assets/videos/rotating-iphone-vp9-with-alpha.webm";
import waterPatternVideo from "../assets/videos/UHD_12mbps.mp4";
import RoomHeader from "../components/RoomHeader";
import { useTranslation } from "../components/Localization";
import { resolveTourPath, useTourMode } from "../components/TourMode";

type FrameSignature = {
  colors: number[];
  luminance: number[];
};

type ReferenceFrame = {
  index: number;
  regions: FrameSignature[];
};

type SequenceMatch = {
  index: number;
  distance: number;
};

const ANALYSIS_SIZE = 192;
const REFERENCE_FRAME_COUNT = 48;
const REGION_GRIDS = [2, 3];
const ANALYSIS_INTERVAL = 180;
// A kijelző fényereje, a kamera fehéregyensúlya és a terem megvilágítása
// jelentősen módosíthatja ugyanannak a videónak a színeit.
const DEFAULT_MATCH_DISTANCE = 0.42;
const REQUIRED_SEQUENCE_MATCHES = 4;
const MAX_SEQUENCE_MISSES = 4;

function createFrameSignature(
  data: Uint8ClampedArray,
  width: number,
  height: number,
): FrameSignature {
  const colors = new Array(48).fill(0) as number[];
  const luminance = new Array(64).fill(0) as number[];
  const luminanceCounts = new Array(64).fill(0) as number[];
  let samples = 0;

  for (let y = 0; y < height; y += 2) {
    for (let x = 0; x < width; x += 2) {
      const index = (y * width + x) * 4;
      const red = data[index] / 255;
      const green = data[index + 1] / 255;
      const blue = data[index + 2] / 255;
      const max = Math.max(red, green, blue);
      const min = Math.min(red, green, blue);
      const delta = max - min;
      let hue = 0;
      if (delta > 0) {
        if (max === red) hue = ((green - blue) / delta + 6) % 6;
        else if (max === green) hue = (blue - red) / delta + 2;
        else hue = (red - green) / delta + 4;
      }
      const hueBin = Math.min(11, Math.floor(hue * 2));
      const saturationBin =
        max === 0 ? 0 : Math.min(1, Math.floor((delta / max) * 2));
      const brightnessBin = Math.min(1, Math.floor(max * 2));
      colors[hueBin * 4 + saturationBin * 2 + brightnessBin] += 1;

      const blockX = Math.min(7, Math.floor((x * 8) / width));
      const blockY = Math.min(7, Math.floor((y * 8) / height));
      const block = blockY * 8 + blockX;
      luminance[block] += red * 0.299 + green * 0.587 + blue * 0.114;
      luminanceCounts[block] += 1;
      samples += 1;
    }
  }

  const spatialAverage =
    luminance.reduce(
      (sum, value, index) => sum + value / Math.max(1, luminanceCounts[index]),
      0,
    ) / luminance.length;

  return {
    colors: colors.map((value) => value / samples),
    // A relatív fényesség kevésbé érzékeny a kamera automatikus expozíciójára.
    luminance: luminance.map(
      (value, index) =>
        value / Math.max(1, luminanceCounts[index]) - spatialAverage,
    ),
  };
}

function signatureDistance(first: FrameSignature, second: FrameSignature) {
  const colorDistance =
    first.colors.reduce(
      (sum, value, index) => sum + Math.abs(value - second.colors[index]),
      0,
    ) / 2;
  const luminanceDistance =
    first.luminance.reduce(
      (sum, value, index) => sum + Math.abs(value - second.luminance[index]),
      0,
    ) / first.luminance.length;
  // A vetítő és a telefon fehéregyensúlya erősen eltolhatja a színeket, ezért
  // a relatív térbeli fényesség megbízhatóbb jel a helyszínen.
  return colorDistance * 0.35 + Math.min(1, luminanceDistance * 3) * 0.65;
}

function drawVideoFrame(
  context: CanvasRenderingContext2D,
  source: HTMLVideoElement,
  size: number,
) {
  const sourceWidth = source.videoWidth;
  const sourceHeight = source.videoHeight;
  context.drawImage(source, 0, 0, sourceWidth, sourceHeight, 0, 0, size, size);
}

function createRegionSignatures(
  data: Uint8ClampedArray,
  width: number,
  height: number,
) {
  const signatures = [createFrameSignature(data, width, height)];
  REGION_GRIDS.forEach((gridSize) => {
    const regionWidth = Math.floor(width / gridSize);
    const regionHeight = Math.floor(height / gridSize);
    for (let gridY = 0; gridY < gridSize; gridY += 1) {
      for (let gridX = 0; gridX < gridSize; gridX += 1) {
        const region = new Uint8ClampedArray(regionWidth * regionHeight * 4);
        for (let y = 0; y < regionHeight; y += 1) {
          const sourceStart =
            ((gridY * regionHeight + y) * width + gridX * regionWidth) * 4;
          region.set(
            data.subarray(sourceStart, sourceStart + regionWidth * 4),
            y * regionWidth * 4,
          );
        }
        signatures.push(
          createFrameSignature(region, regionWidth, regionHeight),
        );
      }
    }
  });
  return signatures;
}

function percentile(values: number[], ratio: number) {
  if (!values.length) return DEFAULT_MATCH_DISTANCE;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * ratio))];
}

function calibrateThreshold(frames: ReferenceFrame[]) {
  const neighboring: number[] = [];
  const unrelated: number[] = [];
  for (let index = 0; index < frames.length; index += 1) {
    const current = frames[index].regions[0];
    neighboring.push(
      signatureDistance(
        current,
        frames[(index + 1) % frames.length].regions[0],
      ),
    );
    unrelated.push(
      signatureDistance(
        current,
        frames[(index + Math.floor(frames.length / 2)) % frames.length]
          .regions[0],
      ),
    );
  }
  const positiveLimit = percentile(neighboring, 0.9) * 2.2;
  const negativeLimit = percentile(unrelated, 0.15) * 0.92;
  return Math.max(0.4, Math.min(0.52, (positiveLimit + negativeLimit) / 2));
}

function findBestMatch(
  cameraRegions: FrameSignature[],
  references: ReferenceFrame[],
  previousIndex?: number,
) {
  let best: SequenceMatch = { index: -1, distance: Number.POSITIVE_INFINITY };
  const candidates =
    previousIndex === undefined
      ? references
      : references.filter((reference) => {
          const advance =
            (reference.index - previousIndex + references.length) %
            references.length;
          return advance <= 8 || advance >= references.length - 1;
        });
  candidates.forEach((reference) => {
    cameraRegions.forEach((cameraRegion) => {
      reference.regions.forEach((referenceRegion) => {
        const distance = signatureDistance(cameraRegion, referenceRegion);
        if (distance < best.distance)
          best = { index: reference.index, distance };
      });
    });
  });
  return best;
}

function advancesSequence(
  previous: number,
  current: number,
  frameCount: number,
) {
  const advance = (current - previous + frameCount) % frameCount;
  return advance <= 5 || advance >= frameCount - 1;
}

function sequenceHasProgress(sequence: SequenceMatch[], frameCount: number) {
  let progress = 0;
  for (let index = 1; index < sequence.length; index += 1) {
    const advance =
      (sequence[index].index - sequence[index - 1].index + frameCount) %
      frameCount;
    if (advance > 0 && advance <= 5) progress += advance;
  }
  return progress >= 2;
}

export default function WarFigureTaskPage() {
  const { interactive } = useTourMode();
  const { room, t } = useTranslation();
  const instruction = room("war")?.tasks.figure;
  const nextTo = resolveTourPath("/vilaghaboru/eredmeny", 1, interactive);
  const isAppleMobile =
    /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const selectedCharacterVideo = isAppleMobile
    ? characterVideoHevc
    : characterVideoWebm;
  const cameraRef = useRef<HTMLVideoElement>(null);
  const characterRef = useRef<HTMLVideoElement>(null);
  const patternRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previousFrameRef = useRef<Uint8ClampedArray | undefined>(undefined);
  const referenceFramesRef = useRef<ReferenceFrame[]>([]);
  const matchThresholdRef = useRef(DEFAULT_MATCH_DISTANCE);
  const sequenceRef = useRef<SequenceMatch[]>([]);
  const sequenceMissesRef = useRef(0);
  const [cameraState, setCameraState] = useState<
    "starting" | "ready" | "error"
  >("starting");
  const [patternFound, setPatternFound] = useState(false);
  const [referenceReady, setReferenceReady] = useState(false);
  const [soundBlocked, setSoundBlocked] = useState(false);

  const playCharacter = useCallback(async () => {
    const video = characterRef.current;
    if (!video) return;
    video.currentTime = 0;
    try {
      await video.play();
      setSoundBlocked(false);
    } catch {
      video.muted = true;
      await video.play().catch(() => undefined);
      setSoundBlocked(true);
    }
  }, []);

  useEffect(() => {
    const video = patternRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    let cancelled = false;

    const seek = (time: number) =>
      new Promise<void>((resolve) => {
        video.addEventListener("seeked", () => resolve(), { once: true });
        video.currentTime = time;
      });

    async function createReferenceSignatures() {
      if (
        !video ||
        !canvas ||
        !Number.isFinite(video.duration) ||
        video.duration <= 0
      )
        return;
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) return;
      const frames: ReferenceFrame[] = [];
      for (let index = 0; index < REFERENCE_FRAME_COUNT; index += 1) {
        const time = (video.duration * (index + 0.5)) / REFERENCE_FRAME_COUNT;
        await seek(time);
        if (cancelled) return;
        drawVideoFrame(context, video, ANALYSIS_SIZE);
        const frame = context.getImageData(
          0,
          0,
          ANALYSIS_SIZE,
          ANALYSIS_SIZE,
        ).data;
        frames.push({
          index,
          regions: createRegionSignatures(frame, ANALYSIS_SIZE, ANALYSIS_SIZE),
        });
      }
      referenceFramesRef.current = frames;
      matchThresholdRef.current = calibrateThreshold(frames);
      setReferenceReady(true);
    }

    if (video.readyState >= HTMLMediaElement.HAVE_METADATA)
      createReferenceSignatures();
    else
      video.addEventListener("loadedmetadata", createReferenceSignatures, {
        once: true,
      });

    return () => {
      cancelled = true;
      video.removeEventListener("loadedmetadata", createReferenceSignatures);
    };
  }, []);

  useEffect(() => {
    let stream: MediaStream | undefined;
    let cancelled = false;

    async function startCamera() {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        if (cameraRef.current) {
          cameraRef.current.srcObject = stream;
          await cameraRef.current.play();
          setCameraState("ready");
        }
      } catch {
        setCameraState("error");
      }
    }

    startCamera();

    return () => {
      cancelled = true;
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  useEffect(() => {
    if (cameraState !== "ready" || !referenceReady || patternFound) return;

    const interval = window.setInterval(() => {
      const video = cameraRef.current;
      const canvas = canvasRef.current;
      if (
        !video ||
        !canvas ||
        video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA
      )
        return;

      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) return;
      drawVideoFrame(context, video, canvas.width);
      const frame = context.getImageData(
        0,
        0,
        canvas.width,
        canvas.height,
      ).data;
      const previous = previousFrameRef.current;
      let changedPixels = 0;
      let sampledPixels = 0;

      // A kis felbontású mintavétel olcsó marad régebbi telefonokon is.
      for (let index = 0; index < frame.length; index += 16) {
        const red = frame[index];
        const green = frame[index + 1];
        const blue = frame[index + 2];
        if (previous) {
          const difference =
            Math.abs(red - previous[index]) +
            Math.abs(green - previous[index + 1]) +
            Math.abs(blue - previous[index + 2]);
          if (difference > 38) changedPixels += 1;
        }
        sampledPixels += 1;
      }

      previousFrameRef.current = new Uint8ClampedArray(frame);
      const sequence = sequenceRef.current;
      const previousMatch = sequence[sequence.length - 1];
      const match = findBestMatch(
        createRegionSignatures(frame, canvas.width, canvas.height),
        referenceFramesRef.current,
        previousMatch?.index,
      );
      const resemblesPattern = match.distance < matchThresholdRef.current;
      const isMoving = previous ? changedPixels / sampledPixels > 0.003 : true;
      const inSequence =
        !previousMatch ||
        advancesSequence(
          previousMatch.index,
          match.index,
          referenceFramesRef.current.length,
        );

      // A mozgás segítő jel, de a korabeli felvételek hosszabb statikus részei
      // miatt nem lehet minden egyes elfogadott képkockánál kötelező.
      if (resemblesPattern && inSequence && (isMoving || sequence.length > 0)) {
        sequence.push(match);
        if (sequence.length > REQUIRED_SEQUENCE_MATCHES) sequence.shift();
        sequenceMissesRef.current = 0;
      } else {
        sequenceMissesRef.current += 1;
        if (sequenceMissesRef.current > MAX_SEQUENCE_MISSES) sequence.splice(0);
      }

      if (
        sequence.length >= REQUIRED_SEQUENCE_MATCHES &&
        sequenceHasProgress(sequence, referenceFramesRef.current.length)
      )
        setPatternFound(true);
    }, ANALYSIS_INTERVAL);

    return () => window.clearInterval(interval);
  }, [cameraState, patternFound, referenceReady]);

  useEffect(() => {
    if (patternFound) playCharacter();
  }, [patternFound, playCharacter]);

  const enableSound = async () => {
    const video = characterRef.current;
    if (!video) return;
    video.muted = false;
    await video.play();
    setSoundBlocked(false);
  };

  const resetRecognition = () => {
    sequenceRef.current = [];
    sequenceMissesRef.current = 0;
    previousFrameRef.current = undefined;
    setPatternFound(false);
  };

  return (
    <main className="screen war-figure-task">
      <RoomHeader roomId="war" />
      <p className="instruction">{instruction}</p>
      <img className="war-camera-icon" src={camera} alt="" />
      <div className="war-camera-screen">
        <video
          ref={cameraRef}
          className="war-camera-feed"
          autoPlay
          muted
          playsInline
          aria-label="Élő kamerakép"
        />
        <video
          ref={patternRef}
          className="war-pattern-reference"
          src={waterPatternVideo}
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
        />
        <video
          ref={characterRef}
          className={`war-character-video${patternFound ? " is-visible" : ""}`}
          src={selectedCharacterVideo}
          playsInline
          preload="auto"
          onEnded={resetRecognition}
          aria-label="A vízből előbukkanó alak"
        />
        <canvas
          ref={canvasRef}
          className="war-camera-analysis"
          width={ANALYSIS_SIZE}
          height={ANALYSIS_SIZE}
        />
        {(cameraState === "starting" || !referenceReady) && (
          <span className="war-camera-message">
            {cameraState === "starting"
              ? "Kamera indítása…"
              : "Minta betöltése…"}
          </span>
        )}
        {cameraState === "error" && (
          <span className="war-camera-message">
            A kamerához hozzáférés szükséges.
          </span>
        )}
        {soundBlocked && (
          <button
            type="button"
            className="war-camera-sound"
            onClick={enableSound}
          >
            Hang bekapcsolása
          </button>
        )}
      </div>

      <div className="war-camera-status" aria-hidden="true">
        <i className={!patternFound ? "active" : ""} />
        <i className={patternFound ? "active" : ""} />
      </div>
      <Link
        className="war-figure-next"
        to={nextTo}
        aria-label={t("common.done")}
      >
        <img src={check} alt="" />
      </Link>
    </main>
  );
}
